import React, { useState, useEffect, useRef } from "react";
// import styles from "./tagInput.module.css";
import styles from "./multiSelectInput.module.css";

const MultiSelectTagInput = ({
  name,
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  returnType = "id", // "id" or "name"
  manual = false, // Enable manual option creation
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [removingIndex, setRemovingIndex] = useState(null);
  const [lastAddedId, setLastAddedId] = useState(null);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Ensure selected options match by string IDs
  //
  useEffect(() => {
    if (!Array.isArray(value) || !Array.isArray(options)) return;

    // First, find options that exist in the original options array
    const selectedFromOptions = options.filter(
      (opt) => value.includes(opt.id) || value.includes(String(opt.id))
    );

    // Then, find values that don't exist in options (manually created ones)
    // Only if manual prop is enabled
    const manuallyCreatedOptions = manual
      ? value
          .filter((val) => {
            const stringVal = String(val);
            return !options.some(
              (opt) => opt.id === val || opt.id === stringVal
            );
          })
          .map((val) => ({
            id: val,
            name: val,
          }))
      : [];

    // Combine both arrays
    const selected = [...selectedFromOptions, ...manuallyCreatedOptions];

    // Only update state if the selected options have actually changed
    const currentIds = selectedOptions.map((opt) => opt.id);
    const newIds = selected.map((opt) => opt.id);

    if (
      currentIds.length !== newIds.length ||
      currentIds.some((id) => !newIds.includes(id)) ||
      newIds.some((id) => !currentIds.includes(id))
    ) {
      setSelectedOptions(selected);
    }
  }, [value, options]);

  const handleSelect = (option) => {
    const newSelected = [...selectedOptions, option];
    setSelectedOptions(newSelected);
    setLastAddedId(option.id);
    triggerChange(newSelected);
    setSearch("");
    setDropdownOpen(false);
  };

  const handleRemove = (id) => {
    setRemovingIndex(id);
    setTimeout(() => {
      const newSelected = selectedOptions.filter((opt) => opt.id !== id);
      setSelectedOptions(newSelected);
      setRemovingIndex(null);
      triggerChange(newSelected);
    }, 300);
  };

  const triggerChange = (selected) => {
    const payload = selected.map((opt) =>
      returnType === "id" ? opt.id : opt.name
    );
    onChange(name, payload);
  };

  const availableOptions = options.filter(
    (opt) =>
      !selectedOptions.find((sel) => sel.id === opt.id) &&
      opt.name.toLowerCase().includes(search.toLowerCase())
  );

  // If no available options and user has typed something, create a new option
  // Only if manual prop is enabled and the option is not already selected
  const shouldShowCreateOption =
    manual &&
    availableOptions.length === 0 &&
    search.trim() !== "" &&
    !selectedOptions.some(
      (opt) => opt.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleCreateOption = () => {
    if (search.trim() === "") return;

    const newOption = {
      id: search.trim(), // Generate a unique ID
      name: search.trim(),
    };

    handleSelect(newOption);
  };

  return (
    <div className={styles.multi_select_container}>
      <div className={styles.tag_box}>
        <input
          onClick={() => setDropdownOpen(true)}
          placeholder={placeholder}
          className={styles.input_field}
          // onFocus={() => setDropdownOpen(true)}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <div className={styles.tag_wrapper}>
          {selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className={`${styles.tag} 
                                ${
                                  removingIndex === opt.id
                                    ? styles.fade_out
                                    : opt.id === lastAddedId
                                    ? styles.fade_in
                                    : ""
                                }`}
            >
              {opt.name}
              <span
                className={styles.remove_tag}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(opt.id);
                }}
              >
                ✖
              </span>
            </span>
          ))}
        </div>
      </div>

      {dropdownOpen && (
        <div ref={wrapperRef} className={styles.dropdown_wrapper}>
          <ul className={styles.dropdown}>
            {shouldShowCreateOption ? (
              <li className={styles.dropdown_item} onClick={handleCreateOption}>
                Select "{search.trim()}" as new
              </li>
            ) : availableOptions.length === 0 ? (
              <li className={styles.dropdown_item_disabled}>No matches</li>
            ) : (
              availableOptions.map((opt) => (
                <li
                  key={opt.id}
                  className={styles.dropdown_item}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.name}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default React.memo(MultiSelectTagInput);
